import type {
  CivilLitigationSaveStep1,
  CivilLitigationSaveStep2,
  CivilLitigationSaveStep3,
  CivilLitigationSaveStep4,
  CivilLitigationSaveStep5,
  CivilLitigationSaveStep5File,
  CivilLitigationStepsResponse,
  SaveCivilLitigationStepsPayload,
} from '../types/civilLitigationApi';
import type { CivilLitigationDocumentRow, CivilLitigationWizardDraft } from '../types/civilLitigationCaseRequest';
import {
  draftHasOtherDeliverySelected,
} from '../utils/deliveryMethod';
import { isDocumentTypeUuid, isPredefinedDocumentType, resolveDocumentTypeUuid } from '../utils/documentTypeId';
import { looksLikeStorageKey, normalizeDocumentFileFields } from '../utils/fileDisplayName';
import type { CivilLitigationWizardStep } from '../pages/wizard/civilLitigation/civilLitigationConstants';
import { requiresCourtType, isOtherNextStep, isCivilLitigationOriginalCourtsCategory } from '../pages/wizard/civilLitigation/civilLitigationHelpers';
import {
  CIVIL_LITIGATION_CATEGORY_ID,
  FILE_CATEGORY_CIVIL_LITIGATION,
  isOtherCourtType,
} from '../pages/wizard/civilLitigation/civilLitigationConstants';
import {
  parseApplicableAreasFromApi,
  parsePhysicalFileDeliveryMethods,
} from './criminalCaseMapper';
import { buildInstituteApiPayload, mapInstituteFromApiResponse } from '../utils/instituteApiPayload';
import { caseRequestIdForSave, draftResumeStepFromApi, finalizeCaseRequestSavePayload, SaveStepFields, strOrNull } from '../utils/caseRequestPayload';

export type { CivilLitigationWizardStep };

export function caseRequestIdFromResponse(response: CivilLitigationStepsResponse): string {
  const id = response.case_request_id ?? response.caseRequestId;
  return typeof id === 'string' ? id.trim() : '';
}

export function referenceNoFromResponse(response: CivilLitigationStepsResponse): string | undefined {
  const ref = response.reference_no ?? response.referenceNo ?? response.fileReferenceNumber;
  const t = typeof ref === 'string' ? ref.trim() : '';
  return t || undefined;
}

function step1LooksComplete(s1: CivilLitigationSaveStep1 | undefined): boolean {
  return Boolean(
    (s1?.sub_category_id ?? '').trim() &&
      (s1?.province_id ?? '').trim() &&
      (s1?.external_case_reference_number ?? '').trim(),
  );
}

function step2LooksComplete(s2: CivilLitigationSaveStep2 | undefined): boolean {
  return Boolean(
    (s2?.plaintiff_applicant_name ?? '').trim() && (s2?.defendant_respondent_name ?? '').trim(),
  );
}

function step3LooksComplete(s3: CivilLitigationSaveStep3 | undefined): boolean {
  return Boolean((s3?.subject_of_dispute ?? '').trim() && (s3?.urgency_level_id ?? '').trim());
}

function step4LooksComplete(s4: CivilLitigationSaveStep4 | undefined): boolean {
  return Boolean((s4?.physical_file_delivery_method_ids ?? []).filter(Boolean).length);
}

function step5LooksComplete(s5: CivilLitigationSaveStep5 | undefined): boolean {
  const files = step5DocumentsFromApi(s5);
  return files.some(
    (f) =>
      Boolean((f.file_name ?? '').trim() || (f.original_file_name ?? '').trim()) ||
      Boolean((f.reason ?? '').trim()),
  );
}

export function resolveCivilLitigationDraftResumeStep(
  response: CivilLitigationStepsResponse,
): CivilLitigationWizardStep {
  return draftResumeStepFromApi(
    response,
    () => {
  if (response.step1Processed === false) return 1;
  if (response.step2Processed === false) return 2;
  if (response.step3Processed === false) return 3;
  if (response.step4Processed === false) return 4;
  if (response.step5Processed === false) return 5;

  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  if (!step1LooksComplete(s1)) return 1;
  if (!step2LooksComplete(s2)) return 2;
  if (!step3LooksComplete(s3)) return 3;
  if (!step4LooksComplete(s4)) return 4;
  if (!step5LooksComplete(s5)) return 5;
  return 5;
    },
    (step) => step >= 1 && step <= 5,
  );
}

function legalOfficerNameFromStep2(s2: CivilLitigationSaveStep2 | undefined): string | undefined {
  return s2?.legal_office_or_officer_incharge_name ?? s2?.legal_officer_name;
}

function legalOfficerContactFromStep2(s2: CivilLitigationSaveStep2 | undefined): string | undefined {
  return s2?.legal_office_or_officer_incharge_contact_no ?? s2?.legal_officer_contact_number;
}

/**
 * Resolve party body radio selection from step 2 API fields.
 * Returns undefined when the API does not indicate a selection so callers can keep defaults.
 * Empty body-type id keys must not be treated as "not selected" — that wiped the wizard
 * default and caused a false "Select one of the following is required" on case update.
 */
function partyBodySelectionFromStep2(
  s2: CivilLitigationSaveStep2 | undefined,
): { government: boolean; incorporated: boolean } | undefined {
  if (!s2) return undefined;

  if (typeof s2.is_incorporated_body === 'boolean') {
    return { government: !s2.is_incorporated_body, incorporated: s2.is_incorporated_body };
  }

  if (s2.involves_public_corporation === true) {
    return { government: false, incorporated: true };
  }
  if (s2.involves_government_ministry === true) {
    return { government: true, incorporated: false };
  }

  const hasIncorp =
    Boolean((s2.incorporated_body_type_id ?? '').trim()) ||
    Boolean((s2.incorporated_body_type_name ?? '').trim());
  const hasUnincorp =
    Boolean((s2.unincorporated_body_type_id ?? '').trim()) ||
    Boolean((s2.unincorporated_body_type_name ?? '').trim());
  if (hasIncorp) return { government: false, incorporated: true };
  if (hasUnincorp) return { government: true, incorporated: false };

  return undefined;
}

function resolvePartyBodyFlags(
  s2: CivilLitigationSaveStep2 | undefined,
  prev: CivilLitigationWizardDraft,
): { involvesGovernmentBody: 'yes' | 'no'; involvesPublicCorporation: 'yes' | 'no' } {
  const fromApi = partyBodySelectionFromStep2(s2);
  const corp =
    fromApi?.incorporated === true ||
    (fromApi == null && prev.involvesPublicCorporation === 'yes');
  const gov =
    fromApi?.government === true ||
    (fromApi == null && prev.involvesGovernmentBody === 'yes');

  if (corp) return { involvesGovernmentBody: 'no', involvesPublicCorporation: 'yes' };
  if (gov) return { involvesGovernmentBody: 'yes', involvesPublicCorporation: 'no' };
  // Match wizard seed + radio UI default (government)
  return { involvesGovernmentBody: 'yes', involvesPublicCorporation: 'no' };
}

function incidentDateFromStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.incident_occurred_from_date ?? s3?.incident_occurred_from;
}

function incidentDateToStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.incident_occurred_to_date ?? s3?.incident_occurred_to;
}

function nextStepIdFromStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.case_next_step_id ?? s3?.next_step_of_case_id;
}

function nextCaseDateFromStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.case_next_date ?? s3?.next_date_of_case;
}

function nextStepNameFromStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.case_next_step_name ?? s3?.next_step_of_case_name;
}

function apiDocumentTypeId(file: CivilLitigationSaveStep5File): string {
  return (file.document_type_id ?? file.document_id ?? '').trim();
}

function step5DocumentsFromApi(s5: CivilLitigationSaveStep5 | undefined): CivilLitigationSaveStep5File[] {
  return [
    ...(s5?.uploaded_files ?? []),
    ...(s5?.extra_uploaded_files ?? []),
    ...(s5?.documents ?? []),
  ];
}

function yesNoToBool(v: '' | 'yes' | 'no'): boolean | undefined {
  if (v === 'yes') return true;
  if (v === 'no') return false;
  return undefined;
}

function boolToYesNo(v: boolean | undefined | null): '' | 'yes' | 'no' {
  if (v === true) return 'yes';
  if (v === false) return 'no';
  return '';
}

type SavedFileRow = {
  file_name?: string | null;
  original_file_name?: string | null;
  fileName?: string | null;
  originalFileName?: string | null;
  upload_time?: string | null;
  uploadTime?: string | null;
};

function savedFileFields(file: SavedFileRow): { fileName: string; originalFileName: string } {
  const apiFileName = file.file_name ?? file.fileName ?? '';
  const original = file.original_file_name ?? file.originalFileName;
  return normalizeDocumentFileFields(apiFileName, original);
}

function uploadTimeFromSavedFile(file: SavedFileRow): string {
  const raw = file.upload_time ?? file.uploadTime;
  return typeof raw === 'string' ? raw.trim() : '';
}

function step5FileFields(file: CivilLitigationSaveStep5File): { fileName: string; originalFileName: string } {
  return savedFileFields(file);
}

function step5FileNamesForSave(
  fileName: string | null | undefined,
  originalFileName: string | null | undefined,
): { file_name?: string; original_file_name?: string } {
  const a = fileName?.trim() ?? '';
  const b = originalFileName?.trim() ?? '';
  if (!a && !b) return {};

  if (a && looksLikeStorageKey(a)) {
    return { file_name: a, ...(b ? { original_file_name: b } : {}) };
  }
  if (b && looksLikeStorageKey(b)) {
    return { file_name: b, ...(a ? { original_file_name: a } : {}) };
  }

  return { file_name: a, ...(b ? { original_file_name: b } : {}) };
}

function otherMethodFromStep4(s4: CivilLitigationSaveStep4 | undefined): string {
  const raw = s4?.other_method ?? s4?.otherMethod;
  return typeof raw === 'string' ? raw.trim() : '';
}

function otherMethodFromDeliveryRows(s4: CivilLitigationSaveStep4 | undefined): string {
  const raw = s4?.physical_file_delivery_methods;
  if (!raw?.length || typeof raw[0] === 'string') return '';

  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const name = item.delivery_method_name?.trim() ?? '';
    const other = item.other_method?.trim();
    if (name.toLowerCase() === 'other' && other) return other;
  }
  return '';
}

function resolveApplicableAreaIds(
  response: CivilLitigationStepsResponse,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas]) {
    const { ids } = parseApplicableAreasFromApi(src);
    if (ids.length) return ids;
  }
  if (Array.isArray(response.applicableAreaIds) && response.applicableAreaIds.length) {
    return response.applicableAreaIds.filter((id) => typeof id === 'string' && id.trim());
  }
  return prev;
}

function resolveApplicableAreaNames(
  response: CivilLitigationStepsResponse,
  prev: string[],
): string[] {
  for (const src of [response.applicable_areas]) {
    const { names } = parseApplicableAreasFromApi(src);
    if (names.length) return names;
  }
  const named = response.applicable_area_names ?? response.applicableAreaNames;
  if (Array.isArray(named) && named.length) {
    return named.filter((n): n is string => typeof n === 'string' && Boolean(n.trim()));
  }
  return prev;
}

function mapStep1(draft: CivilLitigationWizardDraft): SaveStepFields<CivilLitigationSaveStep1> {
  const instituted = draft.hasActionInstituted === 'yes';
  const sendCourtType = requiresCourtType(
    draft.hasActionInstituted,
    draft.subFileCategoryId,
    [],
    draft.subFileCategoryName,
  );
  return {
    ...buildInstituteApiPayload(draft),
    description: strOrNull(draft.description),
    sub_category_id: strOrNull(draft.subFileCategoryId),
    province_id: strOrNull(draft.provinceId),
    district_id: strOrNull(draft.districtId),
    external_case_reference_number: strOrNull(draft.yourReferenceNumber),
    other_external_case_reference_number: strOrNull(draft.otherExternalReferences),
    has_action_instituted: yesNoToBool(draft.hasActionInstituted),
    court_type_id: sendCourtType ? strOrNull(draft.courtTypeId) : undefined,
    court_type_name: sendCourtType ? strOrNull(draft.courtTypeName) : undefined,
    other_court_type:
      sendCourtType && isOtherCourtType(draft.courtTypeId, draft.courtTypeName)
        ? strOrNull(draft.courtTypeOther)
        : undefined,
    court_id: instituted ? strOrNull(draft.courtId) : undefined,
    court_name: instituted ? strOrNull(draft.courtName) : undefined,
    case_number: strOrNull(draft.caseNumber),
    has_connected_case: yesNoToBool(draft.hasConnectedMatters),
    connected_case_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedCaseNumber) : undefined,
    connected_agd_file_ref_number:
      draft.hasConnectedMatters === 'yes' ? strOrNull(draft.connectedAgdFileRef) : undefined,
  };
}

function mapStep2(draft: CivilLitigationWizardDraft): SaveStepFields<CivilLitigationSaveStep2> {
  const isOriginalCourts = isCivilLitigationOriginalCourtsCategory(draft.fileCategory, draft.subFileCategoryName);
  const corps = draft.publicCorporationNames.map((n) => n.trim()).filter(Boolean);
  const incorporated = draft.involvesPublicCorporation === 'yes';
  return {
    plaintiff_applicant_name: strOrNull(draft.plaintiffName),
    defendant_respondent_name: strOrNull(draft.defendantName),
    is_incorporated_body: incorporated,
    involves_government_ministry: yesNoToBool(draft.involvesGovernmentBody),
    involves_public_corporation: yesNoToBool(draft.involvesPublicCorporation),
    public_corporation_names: !isOriginalCourts && corps.length ? corps : undefined,
    other_involved_institutions: strOrNull(draft.otherInvolvedInstitutions),
    legal_office_or_officer_incharge_name: strOrNull(draft.legalOfficerName),
    legal_office_or_officer_incharge_contact_no: strOrNull(draft.legalOfficerContact),
  };
}

function nextStepOtherFromStep3(s3: CivilLitigationSaveStep3 | undefined): string | undefined {
  return s3?.case_next_step_other ?? s3?.next_step_of_case_other ?? undefined;
}

function mapStep3(draft: CivilLitigationWizardDraft): SaveStepFields<CivilLitigationSaveStep3> {
  const otherSelected = isOtherNextStep(draft.nextStepOfCaseName, draft.nextStepOfCaseId);
  return {
    deadline: strOrNull(draft.deadline),
    subject_of_dispute: strOrNull(draft.subjectOfDispute),
    incident_occurred_from_date: strOrNull(draft.incidentDateFrom),
    incident_occurred_to_date: strOrNull(draft.incidentDateTo),
    case_next_step_id: strOrNull(draft.nextStepOfCaseId),
    case_next_step_other: otherSelected ? strOrNull(draft.nextStepOfCaseOther) : null,
    case_next_date: strOrNull(draft.nextCaseDate),
    urgency_level_id: strOrNull(draft.urgencyLevelId),
    urgency_justification: strOrNull(draft.urgencyJustification),
  };
}

function mapStep4(draft: CivilLitigationWizardDraft): SaveStepFields<CivilLitigationSaveStep4> {
  const ids = draft.deliveryMethodIds.filter(Boolean);
  const step4: CivilLitigationSaveStep4 = {
    physical_file_delivery_method_ids: ids.length ? ids : undefined,
  };

  if (draftHasOtherDeliverySelected(draft)) {
    step4.other_method = strOrNull(draft.deliveryOther);
  }

  return step4;
}

function mapStep5(draft: CivilLitigationWizardDraft): SaveStepFields<CivilLitigationSaveStep5> {
  const uploaded_files = draft.documents
    .filter(
      (d) => {
        const files = Array.isArray(d.files) ? d.files : [];
        return isPredefinedDocumentType(d.documentTypeId, d.checklistItemId) && (files.length > 0 || strOrNull(d.skipReason));
    })
    .flatMap((d) => {
      const files = Array.isArray(d.files) ? d.files : [];
      const reason = strOrNull(d.skipReason);
        const documentTypeId = resolveDocumentTypeUuid(d.documentTypeId, d.checklistItemId);
        if (files.length === 0) {
          return [{ ...(documentTypeId ? { document_type_id: documentTypeId } : {}), ...(reason ? { reason } : {}) }];
        }
        return files.map((f) => {
          const names = step5FileNamesForSave(strOrNull(f.fileName), strOrNull(f.originalFileName));
          return {
            ...(documentTypeId ? { document_type_id: documentTypeId } : {}),
            ...names,
            ...(reason ? { reason } : {}),
          };
        });
      });
  const extra_uploaded_files = draft.documents
    .filter((d) => !isPredefinedDocumentType(d.documentTypeId, d.checklistItemId))
    .flatMap((d) => {
      const files = Array.isArray(d.files) ? d.files : [];
      const reason = strOrNull(d.skipReason);
      if (files.length === 0 && !reason) return [];
      if (files.length === 0) return [{ ...(reason ? { reason } : {}) }];
      return files.map((f) => {
        const names = step5FileNamesForSave(strOrNull(f.fileName), strOrNull(f.originalFileName));
        return { ...names, ...(reason ? { reason } : {}) };
      });
    });
  return {
    ...(uploaded_files.length ? { uploaded_files } : {}),
    ...(extra_uploaded_files.length ? { extra_uploaded_files } : {}),
  };
}

export function buildSaveCivilLitigationPayload(
  draft: CivilLitigationWizardDraft,
  options: {
    currentStep?: CivilLitigationWizardStep;
    isDraft: boolean;
    confirmedCorrect?: boolean;
    allSteps?: boolean;
    isUpdateAlreadySubmitted?: boolean;
  },
): SaveCivilLitigationStepsPayload {
  const areaIds = draft.applicableAreaIds.filter(Boolean);
  const payload: SaveCivilLitigationStepsPayload = {
    is_draft_status: options.isDraft,
  };
  const caseRequestId = caseRequestIdForSave(draft.draftRequestId);
  if (caseRequestId) payload.case_request_id = caseRequestId;

  if (options.isUpdateAlreadySubmitted) payload.is_update_already_submitted = true;
  if (options.confirmedCorrect != null) payload.confirmed_correct = options.confirmedCorrect;
  if (caseRequestId || areaIds.length) payload.applicable_areas = areaIds.length ? areaIds : null;

  if (options.allSteps) {
    payload.step_1 = mapStep1(draft);
    payload.step_2 = mapStep2(draft);
    payload.step_3 = mapStep3(draft);
    payload.step_4 = mapStep4(draft);
    payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  const step = options.currentStep;
  if (!step) return finalizeCaseRequestSavePayload(payload, options);

  if (options.isDraft) {
    if (step >= 1) payload.step_1 = mapStep1(draft);
    if (step >= 2) payload.step_2 = mapStep2(draft);
    if (step >= 3) payload.step_3 = mapStep3(draft);
    if (step >= 4) payload.step_4 = mapStep4(draft);
    if (step >= 5) payload.step_5 = mapStep5(draft);
    return finalizeCaseRequestSavePayload(payload, options);
  }

  if (step === 1) payload.step_1 = mapStep1(draft);
  if (step === 2) payload.step_2 = mapStep2(draft);
  if (step === 3) payload.step_3 = mapStep3(draft);
  if (step === 4) payload.step_4 = mapStep4(draft);
  if (step === 5) payload.step_5 = mapStep5(draft);

  return finalizeCaseRequestSavePayload(payload, options);
}

export function mapCivilLitigationStepsToDraft(
  response: CivilLitigationStepsResponse,
  prev: CivilLitigationWizardDraft,
): CivilLitigationWizardDraft {
  const s1 = response.step_1;
  const s2 = response.step_2;
  const s3 = response.step_3;
  const s4 = response.step_4;
  const s5 = response.step_5;

  const documents = (() => {
    const fromApi = step5DocumentsFromApi(s5);
    if (!fromApi.length) return prev.documents;
    if (!prev.documents.length) {
      const grouped = new Map<string, CivilLitigationSaveStep5File[]>();
      for (const f of fromApi) {
        const typeId = apiDocumentTypeId(f);
        const isValid = typeId ? isDocumentTypeUuid(typeId) : Boolean((f.file_name ?? '').trim() || (f.reason ?? '').trim());
        if (!isValid) continue;
        const key = typeId || `__no_type__${grouped.size}`;
        const list = grouped.get(key) ?? [];
        list.push(f);
        grouped.set(key, list);
      }

      return Array.from(grouped.entries()).map(([typeId, entries]) => {
        const files = entries
          .map((f) => {
            const { fileName, originalFileName } = step5FileFields(f);
            return fileName ? { fileName, originalFileName, uploadTime: f.upload_time ?? '' } : null;
          })
          .filter((f): f is { fileName: string; originalFileName: string; uploadTime: string } => f !== null);

        const first = entries[0];
        return {
          id: (first.case_request_document_id ?? '').trim() || crypto.randomUUID(),
          checklistItemId: '',
          documentTypeId: typeId.startsWith('__no_type__') ? '' : typeId,
          documentName: first.document_type_name?.trim() || 'Document',
          mandatory: false,
          files,
          skipReason: entries.find((e) => (e.reason ?? '').trim())?.reason ?? '',
        };
      });
    }
    return prev.documents.map((doc) => {
      const matches = fromApi.filter((f) => {
        const typeId = apiDocumentTypeId(f);
        if (typeId && typeId === doc.documentTypeId) return true;
        if (
          !isDocumentTypeUuid(doc.documentTypeId) &&
          f.document_type_name &&
          doc.documentName &&
          f.document_type_name.trim().toLowerCase() === doc.documentName.trim().toLowerCase()
        ) return true;
        return false;
      });
      if (!matches.length) return doc;
      const files = matches
        .map((match) => {
          const { fileName, originalFileName } = step5FileFields(match);
          return fileName ? { fileName, originalFileName, uploadTime: match.upload_time ?? '' } : null;
        })
        .filter((f): f is { fileName: string; originalFileName: string; uploadTime: string } => f !== null);
      const skipReason = matches[0].reason ?? doc.skipReason;
      return {
        ...doc,
        documentTypeId: apiDocumentTypeId(matches[0]) || doc.documentTypeId,
        files: files.length > 0 ? files : (Array.isArray(doc.files) ? doc.files : []),
        skipReason,
      };
    });
  })();

  const delivery = parsePhysicalFileDeliveryMethods(s4?.physical_file_delivery_methods);
  const deliveryIds = s4?.physical_file_delivery_method_ids?.length
    ? s4.physical_file_delivery_method_ids
    : delivery.ids;

  const corps = s2?.public_corporation_names?.filter((n) => typeof n === 'string' && n.trim()) ?? [];

  const fileCategoryId =
    (response.file_category_id ?? s1?.category_id ?? prev.fileCategoryId) || CIVIL_LITIGATION_CATEGORY_ID;
  const fileCategoryName =
    (response.file_category_name ?? s1?.category_name ?? prev.fileCategory) || FILE_CATEGORY_CIVIL_LITIGATION;
  const subFileCategoryId =
    response.file_sub_category_id ?? s1?.sub_category_id ?? prev.subFileCategoryId;
  const subFileCategoryName =
    response.file_sub_category_name ?? s1?.sub_category_name ?? prev.subFileCategoryName;

  const partyBody = resolvePartyBodyFlags(s2, prev);

  return {
    ...prev,
    ...mapInstituteFromApiResponse({ ...response, ...s1 }, prev),
    draftRequestId: caseRequestIdFromResponse(response) || prev.draftRequestId,
    referenceNo: referenceNoFromResponse(response) ?? prev.referenceNo,
    fileCategory: fileCategoryName,
    fileCategoryId,
    subFileCategoryId,
    subFileCategoryName,
    applicableAreaIds: resolveApplicableAreaIds(response, prev.applicableAreaIds),
    applicableAreas: resolveApplicableAreaNames(response, prev.applicableAreas),
    description: s1?.description ?? prev.description,
    provinceId: s1?.province_id ?? prev.provinceId,
    provinceName: s1?.province_name ?? prev.provinceName,
    districtId: s1?.district_id ?? prev.districtId,
    districtName: s1?.district_name ?? prev.districtName,
    yourReferenceNumber: s1?.external_case_reference_number ?? prev.yourReferenceNumber,
    otherExternalReferences:
      s1?.other_external_case_reference_number ?? prev.otherExternalReferences,
    hasActionInstituted: boolToYesNo(s1?.has_action_instituted) || prev.hasActionInstituted,
    courtTypeId: s1?.court_type_id ?? prev.courtTypeId,
    courtTypeName: s1?.court_type_name ?? prev.courtTypeName,
    courtTypeOther: s1?.other_court_type ?? prev.courtTypeOther,
    courtId: s1?.court_id ?? prev.courtId,
    courtName: s1?.court_name ?? prev.courtName,
    caseNumber: s1?.case_number ?? prev.caseNumber,
    hasConnectedMatters: boolToYesNo(s1?.has_connected_case) || prev.hasConnectedMatters,
    connectedCaseNumber: s1?.connected_case_number ?? prev.connectedCaseNumber,
    connectedAgdFileRef: s1?.connected_agd_file_ref_number ?? prev.connectedAgdFileRef,
    plaintiffName: s2?.plaintiff_applicant_name ?? prev.plaintiffName,
    defendantName: s2?.defendant_respondent_name ?? prev.defendantName,
    involvesGovernmentBody: partyBody.involvesGovernmentBody,
    involvesPublicCorporation: partyBody.involvesPublicCorporation,
    publicCorporationNames: corps.length ? corps : prev.publicCorporationNames,
    otherInvolvedInstitutions: s2?.other_involved_institutions ?? prev.otherInvolvedInstitutions,
    legalOfficerName: legalOfficerNameFromStep2(s2) ?? prev.legalOfficerName,
    legalOfficerContact: legalOfficerContactFromStep2(s2) ?? prev.legalOfficerContact,
    subjectOfDispute: s3?.subject_of_dispute ?? prev.subjectOfDispute,
    incidentDateFrom: incidentDateFromStep3(s3) ?? prev.incidentDateFrom,
    incidentDateTo: incidentDateToStep3(s3) ?? prev.incidentDateTo,
    nextStepOfCaseId: nextStepIdFromStep3(s3) ?? prev.nextStepOfCaseId,
    nextStepOfCaseName: nextStepNameFromStep3(s3) ?? prev.nextStepOfCaseName,
    nextStepOfCaseOther: nextStepOtherFromStep3(s3) ?? prev.nextStepOfCaseOther,
    nextCaseDate: nextCaseDateFromStep3(s3) ?? prev.nextCaseDate,
    urgencyLevelId: s3?.urgency_level_id ?? prev.urgencyLevelId,
    urgencyLevelName: s3?.urgency_level_name ?? prev.urgencyLevelName,
    deadline: s3?.deadline ?? prev.deadline,
    urgencyJustification: s3?.urgency_justification ?? prev.urgencyJustification,
    deliveryMethodIds: deliveryIds.length ? deliveryIds : prev.deliveryMethodIds,
    deliveryMethods: delivery.names.length ? delivery.names : prev.deliveryMethods,
    deliveryOther: otherMethodFromStep4(s4) || otherMethodFromDeliveryRows(s4) || prev.deliveryOther,
    documents,
  };
}
